import React from "react";
import { useIntl } from "react-intl";
import {
  Box,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import {
  FieldGroupTitle,
  MasterField,
  OptionItems,
  OverrideCountChip,
  SectionHeader,
} from "../components/primitives";
import {
  CURRENCY_OPTIONS,
  DISTRIBUTION_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  REFERENCE_FIELDS,
} from "../constants/options";
import { isInvoiceDeliveryReady } from "../model/organizationModel";
import { countChangedFields } from "../model/overrideModel";
import {
  compactInputSx,
  compactMultilineSx,
  dividerSx,
  panelSx,
  ui,
} from "../styles";

const BillingSubsectionHeader = ({ description, title }) => (
  <Box sx={{ mb: 1.05, minWidth: 0 }}>
    <Stack
      direction="row"
      spacing={0.55}
      alignItems="baseline"
      sx={{ minWidth: 0 }}
    >
      <Typography
        sx={{
          color: ui.text,
          fontSize: 10.7,
          lineHeight: 1.25,
          fontWeight: 650,
          flexShrink: 0,
        }}
      >
        {title}
      </Typography>

      {description && (
        <Typography
          sx={{
            color: ui.subtle,
            fontSize: 8.9,
            lineHeight: 1.4,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {description}
        </Typography>
      )}
    </Stack>
  </Box>
);

const BillingStep = ({
  baseline,
  draft,
  selectedOrganization,
  updateField,
}) => {
  const intl = useIntl();

  const t = (id, values) =>
    intl.formatMessage({ id }, values);

  const master = baseline || {};
  const currentDraft = draft || {};

  const paymentOverrideCount = countChangedFields(
    currentDraft,
    master,
    ["paymentMethod", "paymentTermsDays", "currencyCode"]
  );

  const distributionOverrideCount = countChangedFields(
    currentDraft,
    master,
    ["distributionType", "invoiceEmail"]
  );

  const masterField = (field, labelId, props = {}) => (
    <MasterField
      compact
      field={field}
      label={t(labelId)}
      masterValue={master[field]}
      onChange={(event) =>
        updateField(field, event.target.value)
      }
      onReset={updateField}
      value={currentDraft[field]}
      {...props}
    />
  );

  return (
    <Box>
      <SectionHeader
        title={t("admin.crm.customerCard.billing.title")}
        description={t(
          "admin.crm.customerCard.billing.description"
        )}
      />

      <Stack spacing={0.85}>
        <Box
          sx={{
            ...panelSx,
            p: { xs: 1.05, sm: 1.15 },
          }}
        >
          <FieldGroupTitle>
            {t(
              "admin.crm.customerCard.billing.paymentAndDistribution"
            )}
          </FieldGroupTitle>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(0, 1fr) minmax(0, 1fr)",
              },
              columnGap: {
                xs: 0,
                md: 1.5,
              },
              rowGap: 1,
            }}
          >
            <Box
              sx={{
                minWidth: 0,
                pr: { xs: 0, md: 1.5 },
                borderRight: {
                  xs: "none",
                  md: `1px solid ${ ui.border } `,
                },
              }}
            >
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                spacing={0.75}
              >
                <BillingSubsectionHeader
                  title={t(
                    "admin.crm.customerCard.billing.payment"
                  )}
                />

                <OverrideCountChip
                  count={paymentOverrideCount}
                />
              </Stack>

              <Grid container spacing={0.7} sx={{ mt: 0.15 }}>
                <Grid item xs={12} sm={6}>
                  {masterField(
                    "paymentMethod",
                    "admin.crm.customerCard.fields.paymentMethod",
                    {
                      required: true,
                      select: true,
                      children: OptionItems({ options: PAYMENT_METHOD_OPTIONS })                                                                        
                    }
                  )}
                </Grid>

                <Grid item xs={12} sm={3}>
                  {masterField(
                    "paymentTermsDays",
                    "admin.crm.customerCard.fields.paymentTermsDays",
                    {
                      type: "number",
                    }
                  )}
                </Grid>

                <Grid item xs={12} sm={3}>
                  {masterField(
                    "currencyCode",
                    "admin.crm.customerCard.fields.currency",
                    {
                      select: true,
                      children: OptionItems({ options: CURRENCY_OPTIONS })
                    }
                  )}
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                spacing={0.75}
              >
                <BillingSubsectionHeader
                  title={t(
                    "admin.crm.customerCard.billing.distribution"
                  )}
                  description={t(
                    "admin.crm.customerCard.billing.distributionDescription"
                  )}
                />

                <OverrideCountChip
                  count={distributionOverrideCount}
                />
              </Stack>

              <Grid container spacing={0.7} sx={{ mt: 0.15 }}>
                <Grid item xs={12} sm={5}>
                  {masterField(
                    "distributionType",
                    "admin.crm.customerCard.fields.distributionMethod",
                    {
                      select: true,
                      children: OptionItems({ options: DISTRIBUTION_OPTIONS })
                    }
                  )}
                </Grid>

                <Grid item xs={12} sm={7}>
                  {masterField(
                    "invoiceEmail",
                    "admin.crm.customerCard.fields.invoiceEmail"
                  )}
                </Grid>
              </Grid>

              {selectedOrganization &&
                isInvoiceDeliveryReady(
                  selectedOrganization
                ) && (
                  <Stack
                    direction="row"
                    spacing={0.55}
                    alignItems="center"
                    sx={{
                      mt: 0.65,
                      px: 0.75,
                      py: 0.5,
                      borderRadius: 0.9,
                      bgcolor: "#f3faf6",
                      border: "1px solid #d9eee2",
                    }}
                  >
                    <ReceiptLongOutlinedIcon
                      sx={{
                        color: "#247a4d",
                        fontSize: 13,
                        flexShrink: 0,
                      }}
                    />

                    <Typography
                      sx={{
                        color: "#397257",
                        fontSize: 8.9,
                        lineHeight: 1.4,
                      }}
                    >
                      {t(
                        "admin.crm.customerCard.billing.directInvoiceReady"
                      )}
                    </Typography>
                  </Stack>
                )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            ...panelSx,
            p: { xs: 1.05, sm: 1.15 },
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={0.35}
            alignItems={{
              xs: "flex-start",
              sm: "baseline",
            }}
            justifyContent="space-between"
            sx={{ mb: 0.8 }}
          >
            <Box>
              <FieldGroupTitle>
                {t(
                  "admin.crm.customerCard.billing.referencesAndOrder"
                )}
              </FieldGroupTitle>

              <Typography
                sx={{
                  mt: -0.35,
                  color: ui.subtle,
                  fontSize: 8.9,
                  lineHeight: 1.4,
                }}
              >
                {t(
                  "admin.crm.customerCard.billing.referencesDescription"
                )}
              </Typography>
            </Box>
          </Stack>

          <Grid container spacing={0.7}>
            {REFERENCE_FIELDS.map(([field, labelId]) => (
              <Grid item xs={12} md={6} key={field}>
                <TextField
                  fullWidth
                  label={t(labelId)}
                  value={currentDraft[field] ?? ""}
                  onChange={(event) =>
                    updateField(
                      field,
                      event.target.value
                    )
                  }
                  sx={compactInputSx}
                />
              </Grid>
            ))}
          </Grid>

          <Divider
            sx={{
              ...dividerSx,
              my: 0.9,
            }}
          />

          <BillingSubsectionHeader
            title={t(
              "admin.crm.customerCard.billing.textAndNotes"
            )}
            description={t(
              "admin.crm.customerCard.billing.textAndNotesDescription"
            )}
          />

          <Grid container spacing={0.7}>
            {[
              [
                "invoiceMessage",
                "admin.crm.customerCard.fields.invoiceText",
              ],
              [
                "comment",
                "admin.crm.customerCard.fields.internalNote",
              ],
            ].map(([field, labelId]) => (
              <Grid item xs={12} md={6} key={field}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={4}
                  label={t(labelId)}
                  value={currentDraft[field] ?? ""}
                  onChange={(event) =>
                    updateField(
                      field,
                      event.target.value
                    )
                  }
                  sx={compactMultilineSx}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Stack>
    </Box>
  );
};

export default BillingStep;
